<template>
  <div
    class="callout"
    :class="{
      [`callout--${variant}`]: true,
      [`callout--${mode}`]: true,
      [`callout--${height}`]: height
    }"
  >
    <elIcon
      v-if="icon"
      class="callout__icon"
      :name="icon"
    />
    <div class="callout__inner">
      <slot />
    </div>
  </div>
</template>

<script>
import elIcon from './elIcon.vue'

const VALID_VARIANTS = [
  'inactive',
  'info',
  'success',
  'danger',
  'warn'
]

const VALID_MODES = [
  'dark',
  'light'
]

// these heights correspond to the elActionBtn sizes
const VALID_HEIGHTS = [
  '',
  'xs',
  'sm',
  'md',
  'lg'
]

export default {
  components: {
    elIcon
  },
  props: {
    icon: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      required: true,
      validator: (value) => {
        return VALID_VARIANTS.indexOf(value) !== -1
      }
    },
    mode: {
      type: String,
      default: 'light',
      validator: (value) => {
        return VALID_MODES.includes(value)
      }
    },
    height: {
      type: String,
      default: '',
      validator: (value) => {
        return VALID_HEIGHTS.includes(value)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@ui/styles/src/scss/utils/lib';

$lineHeight: 1.5;

.callout {
  $this: &;

  display: flex;
  align-items: baseline;
  padding: 0.5rem 1rem 0.5rem 1rem;
  border-left-width: 4px;
  border-left-style: solid;
  border-radius: 3px;

  &--inactive {
    color: $grey-25;
    background-color: $grey-75;
    border-color: $grey-50;

    &#{$this}--dark {
      color: $grey-80;
      background-color: $grey-40;
    }
  }

  &--info {
    color: $blue-15;
    background-color: $blue-95;
    border-color: $blue-50;

    &#{$this}--dark {
      color: $blue-90;
      background-color: $blue-20;

      button:hover {
        color: $blue-95;
      }
    }
  }

  &--success {
    color: $green-15;
    background-color: $green-95;
    border-color: $green-50;

    button {
      color: inherit;
    }

    &#{$this}--dark {
      color: $green-90;
      background-color: $green-15;

      button:hover {
        color: $green-90;
      }
    }
  }

  &--warn {
    color: $orange-15;
    background-color: $orange-95;
    border-color: $orange-40;

    :deep(a) {
      color: $blue-30;
    }

    button {
      color: inherit;
    }

    &#{$this}--dark {
      color: $orange-95;
      background-color: $orange-10;

      button:hover,
      button:focus {
        color: $orange-95;
      }
    }
  }

  &--danger {
    color: $red-15;
    background-color: $red-95;
    border-color: $red-50;

    button,
    a {
      color: inherit;
    }

    &#{$this}--dark {
      color: $red-95;
      background-color: $red-15;

      button,
      a {
        color: inherit;
      }
    }
  }

  // this makes the callout the same height as the md size elActionBtn
  &--md {
    padding-top: _rem(7);
    padding-bottom: _rem(7);
  }

  &__icon {
    align-self: flex-start;
    min-width: 0.875rem;
    min-height: $lineHeight * 0.875rem;
    margin-right: 0.25rem;
  }

  &__inner {
    line-height: $lineHeight;
  }
}
</style>
